import React, { useState, useEffect, useRef } from "react";
import "./MultiSelectCheckbox.css";

const MultiSelectCheckbox = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = "Select options",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleAll = (e) => {
    if (e.target.checked) {
      onChange(options.map((option) => option.value));
    } else {
      onChange([]);
    }
  };

  const handleOptionToggle = (value) => {
    const selectedSet = new Set(selectedValues);

    if (selectedSet.has(value)) {
      selectedSet.delete(value);
    } else {
      selectedSet.add(value);
    }

    // Convert back to array maintaining order based on options
    const newSelectedValues = options
      .filter((option) => selectedSet.has(option.value))
      .map((option) => option.value);

    // Only call onChange if the values are actually different
    if (
      newSelectedValues.length !== selectedValues.length ||
      !newSelectedValues.every((v, i) => v === selectedValues[i])
    ) {
      onChange(newSelectedValues);
    }
  };

  const getButtonText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === options.length) {
      return "All Selected";
    }
    if (selectedValues.length === 1) {
      const selectedOption = options.find(
        (opt) => opt.value === selectedValues[0]
      );
      return selectedOption ? selectedOption.label : "1 Selected";
    }
    return `${selectedValues.length} Selected`;
  };

  const allSelected = selectedValues.length === options.length;
  const indeterminate = selectedValues.length > 0 && !allSelected;

  return (
    <div className="multi-select-checkbox-container" ref={wrapperRef}>
      {label && <label className="multi-select-label">{label}</label>}
      <button
        type="button"
        className="multi-select-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
        title={getButtonText()}
      >
        <span className="button-text">{getButtonText()}</span>
        <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>
          &#9662;
        </span>
      </button>
      {isOpen && (
        <div className="multi-select-dropdown">
          <div className="multi-select-option">
            <input
              type="checkbox"
              id={`${label}-select-all`}
              checked={allSelected}
              ref={(el) => el && (el.indeterminate = indeterminate)}
              onChange={handleToggleAll}
            />
            <label htmlFor={`${label}-select-all`}>All</label>
          </div>
          {options.map((option) => (
            <div key={option.value} className="multi-select-option">
              <input
                type="checkbox"
                id={`${label}-${option.value}`}
                value={option.value}
                checked={selectedValues.includes(option.value)}
                onChange={() => handleOptionToggle(option.value)}
              />
              <label htmlFor={`${label}-${option.value}`}>{option.label}</label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectCheckbox;
